import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import api, { getErrorMessage } from '../../../services/api';
import { XCircleIcon, TrashIcon } from '@heroicons/react/24/outline';

const PAGE_SIZE = 20;

const MetricCard = ({ label, value, accent = 'text-slate-800' }) => (
  <div className='bg-white rounded-lg border border-slate-200 p-4'>
    <p className='text-xs font-medium text-slate-500 uppercase tracking-wide'>{label}</p>
    <p className={`mt-1 text-2xl font-bold ${accent}`}>{value}</p>
  </div>
);

// Estado de EJECUCIÓN del plan (no solo de validación).
const EXEC_META = {
  executed: { label: 'Ejecutado', cls: 'text-emerald-700 bg-emerald-50' },
  pending: { label: 'Pendiente', cls: 'text-slate-600 bg-slate-100' },
  partial: { label: 'Parcial', cls: 'text-blue-700 bg-blue-50' },
  failed: { label: 'Fallido', cls: 'text-red-700 bg-red-50' },
  requires_review: { label: 'Revisión', cls: 'text-amber-700 bg-amber-50' },
  no_actions: { label: 'Sin acciones', cls: 'text-slate-500 bg-slate-100' },
};

// El veredicto humano: qué se decidió y QUIÉN lo decidió. Guardado queda el id
// del usuario; sin el nombre aquí, se sabe que alguien aprobó pero no quién —
// que es justo lo que hay que poder preguntar cuando una decisión sale mal.
const REVIEW_META = {
  approved: { label: 'Aprobado', cls: 'text-emerald-700 bg-emerald-50' },
  rejected: { label: 'Rechazado', cls: 'text-red-700 bg-red-50' },
  unknown: { label: 'No lo sé', cls: 'text-slate-600 bg-slate-100' },
  pending: { label: 'Esperando', cls: 'text-amber-700 bg-amber-50' },
  auto: { label: 'Automático', cls: 'text-slate-500 bg-slate-50' },
};

const ReviewCell = ({ plan }) => {
  const r = plan.review || {};
  const meta = REVIEW_META[r.status] || REVIEW_META.auto;
  return (
    <div>
      <span className={`inline-flex px-2 py-0.5 rounded-lg text-xs font-medium ${meta.cls}`}>
        {meta.label}
      </span>
      {plan.reviewer_name && (
        <span className='block text-xs text-slate-500 mt-0.5'>
          {plan.reviewer_name}
          {typeof r.seconds_spent === 'number' ? ` · ${r.seconds_spent}s` : ''}
        </span>
      )}
      {r.reason && (
        <span className='block text-xs text-slate-400 truncate max-w-[220px]' title={r.reason}>
          «{r.reason}»
        </span>
      )}
    </div>
  );
};

const ExecBadge = ({ status }) => {
  const meta = EXEC_META[status] || EXEC_META.pending;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-medium ${meta.cls}`}>
      {meta.label}
    </span>
  );
};

/**
 * Pestaña "Planes" del agente: métricas + planes generados (Plan-and-Execute) con
 * detalle de acciones. Consume la API de observabilidad /ai-agent.
 */
const AIAgentPlans = () => {
  const [metrics, setMetrics] = useState(null);
  const [plans, setPlans] = useState([]);
  const [meta, setMeta] = useState({ page: 1, totalPages: 1, total: 0 });
  const [page, setPage] = useState(1);
  const [onlyReview, setOnlyReview] = useState(false);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const loadMetrics = useCallback(async () => {
    try {
      const { data } = await api.get('/ai-agent/metrics');
      setMetrics(data.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }, []);

  const loadPlans = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, pageSize: PAGE_SIZE };
      if (onlyReview) params.requires_review = 'true';
      const { data } = await api.get('/ai-agent/plans', { params });
      setPlans(data.data);
      setMeta(data.meta);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [page, onlyReview]);

  useEffect(() => {
    loadMetrics();
  }, [loadMetrics]);
  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  const handleDelete = async (plan, action) => {
    setDeleting(true);
    try {
      await api.delete(`/ai-agent/plans/${plan._id}`, { params: { assignment_action: action } });
      toast.success(
        action === 'pending'
          ? 'Plan eliminado. El assignment vuelve a pendiente.'
          : action === 'cancel'
            ? 'Plan eliminado y assignment cancelado.'
            : 'Plan eliminado.'
      );
      setSelected(null);
      setConfirmDelete(false);
      loadPlans();
      loadMetrics();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setDeleting(false);
    }
  };

  const closeDrawer = () => {
    setSelected(null);
    setConfirmDelete(false);
  };

  const m = metrics || {};
  const actions = m.actions || {};
  const jobs = m.jobs || {};

  return (
    <div>
      <div className='grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6'>
        <MetricCard label='Planes' value={m.plans ?? '—'} />
        <MetricCard label='Pend. revisión' value={m.plans_requiring_review ?? '—'} accent='text-amber-600' />
        <MetricCard label='Acciones OK' value={actions.success ?? 0} accent='text-emerald-600' />
        <MetricCard label='Acciones fallidas' value={actions.failed ?? 0} accent='text-red-600' />
        <MetricCard label='Jobs pending' value={jobs.pending ?? 0} />
        <MetricCard label='Jobs failed' value={jobs.failed ?? 0} accent='text-red-600' />
      </div>

      <div className='flex items-center justify-between mb-3'>
        <label className='flex items-center gap-2 text-sm text-slate-700'>
          <input
            type='checkbox'
            checked={onlyReview}
            onChange={e => {
              setPage(1);
              setOnlyReview(e.target.checked);
            }}
            className='rounded-lg border-slate-300'
          />
          Solo pendientes de revisión
        </label>
        <span className='text-xs text-slate-500'>{meta.total} planes</span>
      </div>

      <div className='bg-white rounded-lg border border-slate-200 overflow-hidden'>
        <div className='overflow-x-auto'>
          <table className='table-auto w-full text-sm'>
            <thead className='text-xs font-semibold uppercase text-slate-500 bg-slate-50 border-b border-slate-200'>
              <tr>
                <th className='px-3 py-3 text-left'>Fecha</th>
                <th className='px-3 py-3 text-left'>Trigger</th>
                <th className='px-3 py-3 text-left'>Acciones</th>
                <th className='px-3 py-3 text-left'>Ejecución</th>
                <th className='px-3 py-3 text-left'>Revisión</th>
              </tr>
            </thead>
            <tbody className='divide-y divide-slate-200'>
              {loading && (
                <tr>
                  <td colSpan={4} className='px-3 py-6 text-center text-slate-400'>
                    Cargando...
                  </td>
                </tr>
              )}
              {!loading && plans.length === 0 && (
                <tr>
                  <td colSpan={4} className='px-3 py-6 text-center text-slate-400'>
                    No hay planes
                  </td>
                </tr>
              )}
              {!loading &&
                plans.map(plan => (
                  <tr key={plan._id} onClick={() => setSelected(plan)} className='cursor-pointer hover:bg-slate-50'>
                    <td className='px-3 py-2 text-slate-600'>
                      {plan.created_at ? new Date(plan.created_at).toLocaleString('es-ES') : '—'}
                    </td>
                    <td className='px-3 py-2 text-slate-800'>{plan.trigger?.type || '—'}</td>
                    <td className='px-3 py-2 text-slate-800'>{plan.plan?.actions?.length ?? 0}</td>
                    <td className='px-3 py-2'>
                      <ExecBadge status={plan.execution} />
                    </td>
                    <td className='px-3 py-2'>
                      <ReviewCell plan={plan} />
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className='flex items-center justify-end gap-2 mt-3'>
        <button
          disabled={page <= 1}
          onClick={() => setPage(p => p - 1)}
          className='px-3 py-1 text-sm rounded-lg border border-slate-300 bg-white disabled:opacity-50'
        >
          Anterior
        </button>
        <span className='text-sm text-slate-600'>
          {meta.page} / {meta.totalPages || 1}
        </span>
        <button
          disabled={page >= (meta.totalPages || 1)}
          onClick={() => setPage(p => p + 1)}
          className='px-3 py-1 text-sm rounded-lg border border-slate-300 bg-white disabled:opacity-50'
        >
          Siguiente
        </button>
      </div>

      {selected && (
        <div className='fixed inset-0 z-50 flex justify-end' onClick={closeDrawer}>
          <div className='absolute inset-0 bg-black/30' />
          <div
            className='relative w-full max-w-lg h-full bg-white shadow-xl overflow-y-auto p-6'
            onClick={e => e.stopPropagation()}
          >
            <div className='flex items-center justify-between mb-4'>
              <div className='flex items-center gap-2'>
                <h2 className='text-lg font-semibold text-slate-900'>Plan</h2>
                <ExecBadge status={selected.execution} />
              </div>
              <button onClick={closeDrawer} className='text-slate-400 hover:text-slate-600'>
                <XCircleIcon className='w-6 h-6' />
              </button>
            </div>

            <dl className='text-sm space-y-1 mb-4'>
              <div className='flex gap-2'>
                <dt className='text-slate-500'>Run:</dt>
                <dd className='font-mono text-xs text-slate-800'>{selected.workflow_run_id}</dd>
              </div>
              <div className='flex gap-2'>
                <dt className='text-slate-500'>Trigger:</dt>
                <dd className='text-slate-800'>{selected.trigger?.type}</dd>
              </div>
            </dl>

            <p className='text-sm font-semibold text-slate-700 mb-1'>Rationale</p>
            <p className='text-sm text-slate-600 mb-4 whitespace-pre-line'>{selected.plan?.rationale || '—'}</p>

            {selected.validation?.requires_review && (
              <div className='mb-4 p-3 rounded-lg bg-amber-50 border border-amber-100 text-sm text-amber-800'>
                Requiere revisión:{' '}
                {(selected.validation.rejected_actions || []).map(r => r.reason).join('; ') || 'caps'}
              </div>
            )}

            <p className='text-sm font-semibold text-slate-700 mb-2'>Acciones ({selected.plan?.actions?.length ?? 0})</p>
            <div className='space-y-2'>
              {(selected.plan?.actions || []).map((a, i) => (
                <div key={i} className='rounded-lg border border-slate-200 p-3'>
                  <p className='text-sm font-medium text-slate-800'>{a.type}</p>
                  <pre className='mt-2 text-xs bg-slate-50 rounded-lg p-2 overflow-x-auto'>
                    {JSON.stringify(a.params, null, 2)}
                  </pre>
                </div>
              ))}
            </div>

            {/* Eliminar plan — un plan ya ejecutado NO se puede borrar */}
            <div className='mt-8 pt-4 border-t border-slate-200'>
              {selected.execution === 'executed' ? (
                <p className='text-xs text-slate-400'>Un plan ya ejecutado no se puede eliminar.</p>
              ) : !confirmDelete ? (
                <button
                  onClick={() => setConfirmDelete(true)}
                  className='inline-flex items-center gap-2 text-sm font-medium text-red-600 hover:text-red-700'
                >
                  <TrashIcon className='w-5 h-5' />
                  Eliminar plan
                </button>
              ) : (
                <div className='rounded-lg bg-red-50 border border-red-100 p-4'>
                  <p className='text-sm font-medium text-slate-800 mb-1'>Eliminar este plan</p>
                  <p className='text-xs text-slate-500 mb-3'>
                    Se borrará el plan y sus acciones registradas. ¿Qué hacemos con el assignment de origen?
                  </p>
                  <div className='flex flex-col gap-2'>
                    {['pending', 'requires_review', 'no_actions'].includes(selected.execution) && (
                      <button
                        disabled={deleting}
                        onClick={() => handleDelete(selected, 'pending')}
                        className='inline-flex items-center justify-center px-3 py-2 text-sm font-medium rounded-lg text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50'
                      >
                        Eliminar y volver el assignment a pendiente (se re-planificará)
                      </button>
                    )}
                    <button
                      disabled={deleting}
                      onClick={() => handleDelete(selected, 'cancel')}
                      className='inline-flex items-center justify-center px-3 py-2 text-sm font-medium rounded-lg text-white bg-red-600 hover:bg-red-700 disabled:opacity-50'
                    >
                      Eliminar y cancelar el assignment
                    </button>
                    <button
                      disabled={deleting}
                      onClick={() => setConfirmDelete(false)}
                      className='inline-flex items-center justify-center px-3 py-2 text-sm font-medium rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50'
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIAgentPlans;
